import { Link } from "react-router-dom";
import { Shield, Zap, Lock, MessageSquare, ArrowRight } from "lucide-react";

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#09090b] text-white overflow-hidden">
      {/* Nav */}
      <header className="border-b border-[#1f1f23]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-emerald-400 rounded-full blink" />
            <span className="font-heading text-xl tracking-tight">PROX</span>
          </div>
          <div className="flex items-center gap-6">
            <Link
              to="/privacy"
              className="text-sm text-zinc-500 hover:text-white transition-colors hidden sm:inline"
            >
              Privacy
            </Link>
            <Link
              to="/terms"
              className="text-sm text-zinc-500 hover:text-white transition-colors hidden sm:inline"
            >
              Terms
            </Link>
            <Link
              to="/signup"
              className="font-mono text-sm bg-white text-black px-4 py-2 hover:bg-zinc-200 transition-colors"
              data-testid="nav-signup-btn"
            >
              Get Started
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="prox-grid absolute inset-0 opacity-30" />
        <div className="relative max-w-6xl mx-auto px-6 pt-24 pb-20 sm:pt-32 sm:pb-28">
          <div className="max-w-3xl">
            <p className="font-mono text-xs text-emerald-400 uppercase tracking-[0.3em] mb-4">
              Shadow Mapping Technology
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08]">
              Your real number.
              <br />
              <span className="text-zinc-500">Completely hidden.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-zinc-400 max-w-xl leading-relaxed">
              PROX gives you a private proxy number that forwards calls and texts
              to your real phone. Every contact gets their own shadow thread.
              No app needed — it works in your native Messages.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 bg-white text-black font-mono text-sm px-6 py-3 hover:bg-zinc-200 transition-colors"
                data-testid="hero-signup-btn"
              >
                Claim Your Number
                <ArrowRight className="w-4 h-4" />
              </Link>
              <span className="font-mono text-xs text-zinc-600">
                $7.99/mo · Cancel anytime
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-t border-[#1f1f23] bg-[#0b0b0d]">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <p className="font-mono text-xs text-zinc-500 uppercase tracking-[0.3em] mb-3">
            How it works
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl tracking-tight mb-12">
            Three layers of privacy
          </h2>

          <div className="grid md:grid-cols-3 gap-px bg-[#1f1f23]">
            <FeatureCard
              icon={<Shield className="w-5 h-5" />}
              title="Proxy Number"
              desc="You get a dedicated phone number. Share it instead of your real one. Texts and calls forward seamlessly."
              testid="feature-proxy"
            />
            <FeatureCard
              icon={<Zap className="w-5 h-5" />}
              title="Shadow Mapping"
              desc="Each contact gets a unique shadow thread on your phone. Separate iMessage conversations, zero confusion."
              testid="feature-shadow"
            />
            <FeatureCard
              icon={<Lock className="w-5 h-5" />}
              title="Burn on Demand"
              desc='Text "BURN" to any shadow thread to permanently block that contact. Your real number stays invisible.'
              testid="feature-burn"
            />
          </div>
        </div>
      </section>

      {/* Commands */}
      <section className="border-t border-[#1f1f23]">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <p className="font-mono text-xs text-zinc-500 uppercase tracking-[0.3em] mb-3">
            Commands
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl tracking-tight mb-10">
            Control via text
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <CommandRow cmd="BURN" desc="Delete mapping & block the contact forever" />
            <CommandRow cmd="SAVE [name]" desc="Label and graduate a contact" />
            <CommandRow cmd="HELP" desc="Get the full command list" />
            <CommandRow cmd="STOP" desc="Contacts can opt out by replying STOP" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#1f1f23] bg-[#0b0b0d]">
        <div className="max-w-6xl mx-auto px-6 py-20 text-center">
          <MessageSquare className="w-8 h-8 text-emerald-400 mx-auto mb-4" />
          <h2 className="font-heading text-2xl sm:text-3xl tracking-tight mb-3">
            Ready to go private?
          </h2>
          <p className="text-zinc-400 text-sm mb-8 max-w-md mx-auto">
            Get your PROX number in under a minute. Invite code required during beta.
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 bg-white text-black font-mono text-sm px-6 py-3 hover:bg-zinc-200 transition-colors"
            data-testid="cta-signup-btn"
          >
            Get Started
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#1f1f23]">
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
            <span className="font-heading text-sm">PROX</span>
            <span className="font-mono text-[10px] text-zinc-600 ml-2">
              by Shadow Bridge Technologies, Inc.
            </span>
          </div>
          <div className="flex gap-6 text-xs text-zinc-500">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/sms-terms" className="hover:text-white transition-colors">SMS Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, title, desc, testid }) {
  return (
    <div className="bg-[#0b0b0d] p-8" data-testid={testid}>
      <div className="text-emerald-400 mb-4">{icon}</div>
      <h3 className="font-heading text-lg text-white mb-2">{title}</h3>
      <p className="text-sm text-zinc-400 leading-relaxed">{desc}</p>
    </div>
  );
}

function CommandRow({ cmd, desc }) {
  return (
    <div className="border border-[#1f1f23] bg-[#0e0e10] p-4 flex items-start gap-4">
      <code className="font-mono text-xs text-emerald-400 bg-[#0b0b0d] px-2 py-1 shrink-0">
        {cmd}
      </code>
      <span className="text-sm text-zinc-400">{desc}</span>
    </div>
  );
}
